/**
 * Tell the API which domain the browser is actually on.
 *
 * The app is served by a static site that rewrites /api/* through to the API
 * service. That rewrite replaces the Host header with its own destination, so
 * the server cannot see whether a visitor arrived on the platform domain or on
 * an auctioneer's custom domain. Tenant branding and isolation both depend on
 * knowing, so the browser states it and the server reads it back.
 *
 * Installed as a fetch wrapper rather than added at each call site: there are
 * around twenty places that call the API, and one missed call is a request that
 * silently loses its tenant. This way new call sites are covered by default.
 *
 * The header is only attached to requests aimed at this app's own API. Nothing
 * is added to third-party requests, where it would leak the hostname and
 * provoke a CORS preflight for no reason.
 */
export const TENANT_HOST_HEADER = 'X-Tenant-Host';

/** Requests to these are ours; anything else is left untouched. */
function isOwnApiRequest(url: string, apiBaseUrl: string): boolean {
  try {
    const target = new URL(url, window.location.href);
    const api = new URL(apiBaseUrl, window.location.href);
    return target.origin === api.origin && target.pathname.startsWith('/api');
  } catch {
    return false;
  }
}

export function installTenantHostHeader(apiBaseUrl: string): void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;

  const original = window.fetch.bind(window);

  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url =
      typeof input === 'string' ? input :
      input instanceof URL ? input.toString() :
      input.url;

    if (!isOwnApiRequest(url, apiBaseUrl)) {
      return original(input as any, init);
    }

    // Headers may arrive as a Headers instance, an array of pairs, or a plain
    // object. Normalising through Headers handles all three without clobbering
    // whatever the caller set.
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined)
    );
    headers.set(TENANT_HOST_HEADER, window.location.hostname);

    return original(input as any, { ...init, headers });
  };
}
